import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Package, Plus, Pencil, Trash2, Save, X, Search } from 'lucide-react';

const InventoryPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', category: 'verktyg', brand_preferred: '', model_preferred: '', quantity: 1, unit: 'st', notes: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.listInventory();
      setItems(r.data?.items || []);
    } catch {
      toast.error('Kunde inte ladda lager');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', category: 'verktyg', brand_preferred: '', model_preferred: '', quantity: 1, unit: 'st', notes: '' });
    setShowDialog(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name || '',
      category: it.category || 'verktyg',
      brand_preferred: it.brand_preferred || '',
      model_preferred: it.model_preferred || '',
      quantity: it.quantity || 1,
      unit: it.unit || 'st',
      notes: it.notes || '',
    });
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Namn krävs'); return; }
    try {
      if (editing) {
        await api.updateInventoryItem(editing.id, form);
        toast.success('Uppdaterad');
      } else {
        await api.createInventoryItem(form);
        toast.success('Tillagd');
      }
      setShowDialog(false);
      load();
    } catch {
      toast.error('Kunde inte spara');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Ta bort från lager?')) return;
    try {
      await api.deleteInventoryItem(id);
      toast.success('Borttagen');
      load();
    } catch {
      toast.error('Kunde inte ta bort');
    }
  };

  const filtered = items.filter(it => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (it.name || '').toLowerCase().includes(s)
      || (it.brand_preferred || '').toLowerCase().includes(s)
      || (it.model_preferred || '').toLowerCase().includes(s)
      || (it.category || '').toLowerCase().includes(s);
  });

  const byCategory = filtered.reduce((acc, it) => {
    const k = it.category || 'övrigt';
    (acc[k] = acc[k] || []).push(it);
    return acc;
  }, {});

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="inventory-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Package size={24} /> Lager</h1>
          <p className="text-xs text-muted-foreground mt-1">Verktyg och förbrukning som firman äger. AI tar hänsyn till detta i framtida verktygsberäkningar.</p>
        </div>
        <Button onClick={openNew} className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="btn-add-inventory">
          <Plus size={14} className="mr-1" /> Lägg till
        </Button>
      </div>

      <Card className="p-3 flex items-center gap-2">
        <Search size={14} className="text-muted-foreground" />
        <Input placeholder="Sök på namn, märke, kategori..." value={q} onChange={(e) => setQ(e.target.value)} className="border-0 shadow-none focus-visible:ring-0" data-testid="inventory-search" />
      </Card>

      {loading ? (
        <p className="text-center text-muted-foreground">Laddar...</p>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          <Package size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">Lagret är tomt. Lägg till verktyg manuellt eller markera "finns i lager" på verktygs­beräkningar i projekt.</p>
        </Card>
      ) : (
        Object.entries(byCategory).map(([cat, list]) => (
          <Card key={cat} className="overflow-hidden">
            <div className="bg-muted px-4 py-2 border-b border-border">
              <h3 className="font-semibold text-sm capitalize">{cat} <span className="text-xs text-muted-foreground font-normal">({list.length})</span></h3>
            </div>
            <div className="divide-y divide-border">
              {list.map(it => (
                <div key={it.id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-muted/20" data-testid={`inventory-item-${it.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{it.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {[it.brand_preferred, it.model_preferred].filter(Boolean).join(' · ') || '— ingen märke/modell —'}
                      {it.notes && ` · ${it.notes}`}
                    </p>
                  </div>
                  <div className="text-right text-xs">
                    <p className="font-semibold">{it.quantity} {it.unit}</p>
                  </div>
                  <button onClick={() => openEdit(it)} className="p-1.5 text-muted-foreground hover:text-foreground" data-testid={`edit-${it.id}`}><Pencil size={14} /></button>
                  <button onClick={() => del(it.id)} className="p-1.5 text-muted-foreground hover:text-destructive" data-testid={`delete-${it.id}`}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </Card>
        ))
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md" data-testid="inventory-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Redigera lagerpost' : 'Lägg till i lager'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Namn *</Label><Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Cirkelsåg" data-testid="inv-name" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Märke</Label><Input value={form.brand_preferred} onChange={(e) => setForm(f => ({ ...f, brand_preferred: e.target.value }))} placeholder="Milwaukee" /></div>
              <div><Label>Modell</Label><Input value={form.model_preferred} onChange={(e) => setForm(f => ({ ...f, model_preferred: e.target.value }))} placeholder="M18 CSX-121" /></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Kategori</Label>
                <select value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} className="w-full h-9 rounded border border-border bg-background px-2 text-sm">
                  <option value="verktyg">Verktyg</option>
                  <option value="förbrukning">Förbrukning</option>
                  <option value="maskin">Maskin</option>
                  <option value="personlig_skydd">Skyddsutr.</option>
                  <option value="arbetskläder">Arbetskläder</option>
                  <option value="övrigt">Övrigt</option>
                </select>
              </div>
              <div><Label>Antal</Label><Input type="number" value={form.quantity} onChange={(e) => setForm(f => ({ ...f, quantity: parseInt(e.target.value) || 1 }))} /></div>
              <div><Label>Enhet</Label><Input value={form.unit} onChange={(e) => setForm(f => ({ ...f, unit: e.target.value }))} placeholder="st" /></div>
            </div>
            <div><Label>Anteckning</Label><Input value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}><X size={14} className="mr-1" /> Avbryt</Button>
            <Button onClick={save} className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="btn-save-inventory">
              <Save size={14} className="mr-1" /> Spara
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryPage;
